import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Alert,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import type { FormEvent } from "react";
import { authFlowHeaderIcons } from "./authFlowHeaderIcons";
import {
  getLoginFieldSx,
  getLoginPrimaryButtonSx,
  LoginCardContent,
  LoginFormHeader,
} from "./loginUi";

export type SetNewPasswordFormProps = {
  onSubmit?: (password: string) => void;
  error?: string;
  loading?: boolean;
  validLink?: boolean;
  loginUrl?: string;
  translations: {
    resetPassword: string;
    newPassword: string;
    confirmPassword: string;
    setNewPassword: string;
    fieldRequired: string;
    passwordsDoNotMatch: string;
    passwordTooShort: string;
    passwordResetUnsuccessful: string;
    passwordResetLinkInvalid: string;
    backToLogin: string;
  };
};

export const SetNewPasswordForm = ({
  onSubmit,
  error,
  loading = false,
  validLink = true,
  loginUrl = "/accounts/login/",
  translations: t,
}: SetNewPasswordFormProps) => {
  const theme = useTheme();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");

  const validate = (): boolean => {
    let valid = true;
    setPasswordError("");
    setConfirmError("");

    if (!password) {
      setPasswordError(t.fieldRequired);
      valid = false;
    } else if (password.length < 8) {
      setPasswordError(t.passwordTooShort);
      valid = false;
    }

    if (!confirmPassword) {
      setConfirmError(t.fieldRequired);
      valid = false;
    } else if (password !== confirmPassword) {
      setConfirmError(t.passwordsDoNotMatch);
      valid = false;
    }

    return valid;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validLink) {
      return;
    }
    if (validate()) {
      onSubmit?.(password);
    }
  };

  const fieldSx = getLoginFieldSx(theme);

  if (!validLink) {
    return (
      <LoginCardContent testId="set-new-password-invalid-link">
        <LoginFormHeader
          title={t.passwordResetUnsuccessful}
          subtitle={t.passwordResetLinkInvalid}
          headerIcon={authFlowHeaderIcons.passwordResetInvalid}
          headerTestId="set-new-password-invalid-header"
          titleTestId="set-new-password-invalid-title"
          headerIconTestId="set-new-password-invalid-header-icon"
        />

        <Button
          href={loginUrl}
          fullWidth
          variant="contained"
          size="large"
          data-testid="invalid-link-back-to-login"
          sx={getLoginPrimaryButtonSx(theme)}
        >
          {t.backToLogin}
        </Button>
      </LoginCardContent>
    );
  }

  return (
    <LoginCardContent
      component="form"
      onSubmit={handleSubmit}
      testId="set-new-password-form"
    >
      <LoginFormHeader
        title={t.resetPassword}
        headerIcon={authFlowHeaderIcons.setNewPassword}
        headerTestId="set-new-password-form-header"
        titleTestId="set-new-password-form-title"
        headerIconTestId="set-new-password-form-header-icon"
      />

      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2.5 }}
          data-testid="set-password-error"
        >
          {error}
        </Alert>
      ) : null}

      <TextField
        fullWidth
        label={t.newPassword}
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!passwordError}
        helperText={passwordError}
        autoComplete="new-password"
        autoFocus
        sx={fieldSx}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            inputProps: { "data-testid": "new-password-input" },
          },
        }}
      />

      <TextField
        fullWidth
        label={t.confirmPassword}
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={!!confirmError}
        helperText={confirmError}
        autoComplete="new-password"
        sx={{ ...fieldSx, mb: 3 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            inputProps: { "data-testid": "confirm-password-input" },
          },
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        data-testid="set-password-submit"
        sx={getLoginPrimaryButtonSx(theme)}
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          t.setNewPassword
        )}
      </Button>
    </LoginCardContent>
  );
};
